import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { scenarioPacks as api, stylePresets as stylePresetsApi } from '../lib/api.js'
import { WizardStepper } from '../components/wizard-stepper.js'
import { DetailEditor } from '../components/detail-editor.js'
import { CriteriaEditor } from '../components/criteria-editor.js'
import { NoiseEditor } from '../components/noise-editor.js'
import { StyleEditor } from '../components/style-editor.js'
import { GenerationPanel } from '../components/generation-panel.js'
import { realtime } from '../lib/realtime.js'

// --- Shared styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const stepContainer = css`
  ${card};
  margin-bottom: ${space.xl};
`

const stepTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const stepDescription = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.lg};
`

const emptyState = css`
  text-align: center;
  padding: ${space.lg};
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
`

const scenarioContext = css`
  border-left: 3px solid ${colors.brand};
  padding: ${space.sm} ${space.md};
  margin-bottom: ${space.lg};
  background: ${colors.bgSidebar};
  border-radius: 0 6px 6px 0;
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  white-space: pre-wrap;
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const detailCheckList = css`
  border: 1px solid ${colors.border};
  border-radius: 6px;
  overflow: hidden;
`

const detailCheckItem = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
  cursor: pointer;
  &:hover { background: ${colors.bg}; }
`

const detailCheckName = css`
  flex: 1;
  font-size: ${font.size.sm};
`

const detailCheckType = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const addDetailBtnStyle = css`
  ${btnSecondary};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
`


export function ScenarioWizardView() {
  let projectId, packId, scenarioId
  let pack = null
  let scenario = null
  let loading = true
  let error = null
  let saving = false

  const STEP_NAMES = ['scenario', 'details', 'rubric', 'noise', 'style', 'generate']

  function getCurrentStep() {
    const stepParam = m.route.param('step') || 'scenario'
    const idx = STEP_NAMES.indexOf(stepParam)
    return idx >= 0 ? idx : 0
  }

  function setStep(idx) {
    const base = `/projects/${projectId}/scenarios/${packId}/${scenarioId}`
    m.route.set(`${base}/${STEP_NAMES[idx] || 'scenario'}`)
  }

  // Step 1 state
  let contentDraft = ''

  // Step 2 state
  let relevantDetailIds = new Set()
  let showAddDetail = false
  let editingDetailId = null

  // Step 3 state
  let criteria = []
  let coverage = null

  // Step 4 state (noise)
  let irrelevantDetails = []  // [{detail_id, inclusion_freq}]
  let noiseSnippets = []

  // Step 5 state (style)
  let stylePresets = []

  // Step 6 state (generation)
  let generationConfig = null
  let generations = []
  let generating = false
  let generationProgress = null  // { completed, total }
  let unsubProgress = null

  async function load() {
    loading = true
    error = null
    try {
      pack = await api.get(projectId, packId)
      scenario = pack.scenarios.find(s => s.id === scenarioId)
      if (!scenario) {
        error = 'Scenario not found'
        loading = false
        m.redraw()
        return
      }
      contentDraft = scenario.content === '(new scenario)' ? '' : scenario.content

      // Load scenario-detail relevance
      const scenarioDetails = (pack.scenarioDetails || []).filter(sd => sd.scenario_id === scenarioId)
      relevantDetailIds = new Set(scenarioDetails.map(sd => sd.detail_id))

      // Load criteria
      criteria = await api.getCriteria(projectId, packId, scenarioId)
      try { coverage = await api.getCoverage(projectId, packId, scenarioId) } catch (e) { coverage = null }

      // Load noise data
      try { irrelevantDetails = await api.getIrrelevantDetails(projectId, packId, scenarioId) } catch (e) { irrelevantDetails = [] }
      try { noiseSnippets = await api.getNoiseSnippets(projectId, packId, scenarioId) } catch (e) { noiseSnippets = [] }

      // Load style presets (project-level)
      try { stylePresets = await stylePresetsApi.list(projectId) } catch (e) { stylePresets = [] }

      // Load generation data
      try { generationConfig = await api.getGenerationConfig(projectId) } catch (e) { generationConfig = null }
      try { generations = await api.getGenerations(projectId, packId, scenarioId) } catch (e) { generations = [] }

      // No auto-advance — the URL determines the step
    } catch (e) {
      error = e.message
    } finally {
      loading = false
      m.redraw()
    }
  }

  function getSteps() {
    return [
      { label: 'Scenario' },
      { label: 'Details' },
      { label: 'Rubric' },
      { label: 'Noise' },
      { label: 'Style' },
      { label: 'Generate' },
    ]
  }

  // Step 1: Save scenario content
  async function saveContent() {
    if (!contentDraft.trim()) return
    saving = true
    try {
      await api.updateScenario(projectId, packId, scenarioId, { content: contentDraft.trim() })
      scenario.content = contentDraft.trim()
      setStep(1)
    } catch (e) {
      error = e.message
    }
    saving = false
    m.redraw()
  }

  // Step 2: Toggle detail relevance
  async function toggleDetail(detailId) {
    const newIds = new Set(relevantDetailIds)
    if (newIds.has(detailId)) {
      newIds.delete(detailId)
    } else {
      newIds.add(detailId)
    }
    try {
      await api.setScenarioDetails(projectId, packId, scenarioId, [...newIds])
      relevantDetailIds = newIds
      // Coverage depends on relevant details — refresh it
      try { coverage = await api.getCoverage(projectId, packId, scenarioId) } catch (e) { coverage = null }
    } catch (e) {
      error = e.message
    }
    m.redraw()
  }

  // Step 2: Create new detail
  async function createDetail(data) {
    const detail = await api.createDetail(projectId, packId, data)
    pack.details.push(detail)
    // Auto-mark as relevant
    relevantDetailIds.add(detail.id)
    await api.setScenarioDetails(projectId, packId, scenarioId, [...relevantDetailIds])
    try { coverage = await api.getCoverage(projectId, packId, scenarioId) } catch (e) { coverage = null }
    showAddDetail = false
    m.redraw()
  }

  // Step 2: Update existing detail
  async function updateDetail(detailId, data) {
    const updated = await api.updateDetail(projectId, packId, detailId, data)
    const idx = pack.details.findIndex(d => d.id === detailId)
    if (idx >= 0) pack.details[idx] = updated
    editingDetailId = null
    m.redraw()
  }

  // Step 3: Refresh criteria and coverage
  async function refreshCriteria() {
    criteria = await api.getCriteria(projectId, packId, scenarioId)
    try { coverage = await api.getCoverage(projectId, packId, scenarioId) } catch (e) { coverage = null }
    m.redraw()
  }

  function renderScenarioContext() {
    if (!scenario || !scenario.content || scenario.content === '(new scenario)') return null
    return m('div', { class: scenarioContext }, scenario.content)
  }

  function renderStep() {
    switch (getCurrentStep()) {
      case 0: return renderStep1()
      case 1: return renderStep2()
      case 2: return renderStep3()
      case 3: return renderStep4()
      case 4: return renderStep5()
      case 5: return renderStep6()
      default: return null
    }
  }

  // --- Step 1: Scenario Content ---
  function renderStep1() {
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 1: Scenario'),
      m('p', { class: stepDescription }, 'Define the scenario text — the question, action, or outcome to test.'),
      m('div', { class: formRow }, [
        m('textarea', {
          class: input,
          rows: 8,
          value: contentDraft,
          oninput: (e) => { contentDraft = e.target.value },
          placeholder: 'Describe the scenario...\n\nExample: "A user asks about the tax implications of selling a home in California after living there for 2 years."',
        }),
      ]),
      m('div', { class: formActions }, [
        m('button', {
          class: btnPrimary,
          onclick: saveContent,
          disabled: saving || !contentDraft.trim(),
        }, saving ? 'Saving...' : scenario.content ? 'Update & Continue' : 'Save & Continue'),
      ]),
    ])
  }

  // --- Step 2: Details ---
  function renderStep2() {
    const details = pack.details || []
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 2: Details'),
      m('p', { class: stepDescription }, 'Select which factual details are relevant to this scenario. You can also create new details.'),
      renderScenarioContext(),

      details.length > 0
        ? m('div', { class: detailCheckList },
            details.map(d => {
              const isEditing = editingDetailId === d.id
              const children = []
              children.push(m('div', {
                key: `row-${d.id}`,
                class: detailCheckItem,
                onclick: () => toggleDetail(d.id),
              }, [
                m('input', {
                  type: 'checkbox',
                  checked: relevantDetailIds.has(d.id),
                  onclick: (e) => e.stopPropagation(),
                  onchange: () => toggleDetail(d.id),
                }),
                m('span', { class: detailCheckName }, [
                  d.name,
                  d.type === 'enum' && d.options
                    ? m('span', { class: detailCheckType, style: 'margin-left: 4px;' },
                        `list (${d.options.map(o => typeof o === 'string' ? o : o.label).join(', ')})`)
                    : m('span', { class: detailCheckType, style: 'margin-left: 4px;' }, d.type === 'enum' ? 'list' : d.type),
                ]),
                m('button', {
                  class: btnSecondary,
                  style: `font-size: ${font.size.xs}; padding: 2px 8px; margin-left: auto;`,
                  onclick: (e) => {
                    e.stopPropagation()
                    editingDetailId = isEditing ? null : d.id
                  },
                }, isEditing ? 'Cancel' : 'Edit'),
              ]))
              if (isEditing) {
                children.push(m('div', {
                  key: `edit-${d.id}`,
                  style: `padding: ${space.md}; border-bottom: 1px solid ${colors.border}; background: ${colors.bgSidebar};`,
                }, [
                  m(DetailEditor, {
                    detail: d,
                    onSave: (data) => updateDetail(d.id, data),
                    onCancel: () => { editingDetailId = null },
                  }),
                ]))
              }
              return children
            }).flat()
          )
        : m('div', { class: emptyState }, 'No details in this pack yet. Create one below.'),

      m('button', {
        class: addDetailBtnStyle,
        onclick: () => { showAddDetail = !showAddDetail },
      }, showAddDetail ? 'Cancel' : '+ Add New Detail'),

      // Inline create detail form
      showAddDetail ? m('div', {
        style: `margin-top: ${space.md}; padding: ${space.md}; border: 1px solid ${colors.border}; border-radius: 6px; background: ${colors.bgCard};`,
      }, [
        m(DetailEditor, {
          detail: null,
          onSave: createDetail,
          onCancel: () => { showAddDetail = false },
        }),
      ]) : null,

      relevantDetailIds.size > 0
        ? m('div', { class: formActions }, [
            m('button', {
              class: btnPrimary,
              onclick: () => { setStep(2) },
            }, 'Continue to Rubric'),
          ])
        : null,
    ])
  }

  // --- Step 3: Rubric (Criteria Editor) ---
  function renderStep3() {
    const relevantDetails = (pack.details || []).filter(d => relevantDetailIds.has(d.id))
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 3: Rubric'),
      m('p', { class: stepDescription }, 'Define evaluation criteria and which detail combinations they apply to.'),
      renderScenarioContext(),
      m(CriteriaEditor, {
        criteria,
        details: relevantDetails,
        coverage,
        onCreateCriterion: async (data) => {
          saving = true; m.redraw()
          try { await api.createCriterion(projectId, packId, scenarioId, data) } catch (e) { error = e.message }
          saving = false; await refreshCriteria()
        },
        onUpdateCriterion: async (criterionId, data) => {
          saving = true; m.redraw()
          try { await api.updateCriterion(projectId, packId, criterionId, data) } catch (e) { error = e.message }
          saving = false; await refreshCriteria()
        },
        onDeleteCriterion: async (criterionId) => {
          try { await api.deleteCriterion(projectId, packId, criterionId) } catch (e) { error = e.message }
          await refreshCriteria()
        },
        onSetScope: async (criterionId, scope) => {
          saving = true; m.redraw()
          try { await api.setCriterionScope(projectId, packId, criterionId, scope) } catch (e) { error = e.message }
          saving = false; await refreshCriteria()
        },
        onSplitCriterion: async (criterionId, newScope) => {
          saving = true; m.redraw()
          try {
            const result = await api.splitCriterion(projectId, packId, criterionId, newScope)
            saving = false; await refreshCriteria()
            return result
          } catch (e) { error = e.message; saving = false; m.redraw(); return null }
        },
        onReorder: async (ids) => {
          try { await api.reorderCriteria(projectId, packId, scenarioId, ids) } catch (e) { error = e.message }
          await refreshCriteria()
        },
        saving,
      }),
      criteria.length > 0
        ? m('div', { class: formActions }, [
            m('button', {
              class: btnPrimary,
              onclick: () => { setStep(3) },
            }, 'Continue to Noise'),
          ])
        : null,
    ])
  }

  // --- Step 4: Noise ---
  function renderStep4() {
    const scenarioDetails = (pack.scenarioDetails || []).filter(sd => sd.scenario_id === scenarioId)
    const relevantDetails = (pack.details || []).filter(d => relevantDetailIds.has(d.id))
    const detailsWithOmission = relevantDetails.map(d => {
      const sd = scenarioDetails.find(sd => sd.detail_id === d.id)
      return { ...d, omission_freq: sd?.omission_freq || 'sometimes' }
    })
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 4: Noise'),
      m('p', { class: stepDescription }, 'Configure how often details are included, add irrelevant details, and create noise snippets.'),
      renderScenarioContext(),
      m(NoiseEditor, {
        relevantDetails: detailsWithOmission,
        allDetails: pack.details || [],
        irrelevantDetails,
        noiseSnippets,
        onSetOmissionFreq: async (detailId, freq) => {
          saving = true; m.redraw()
          try {
            await api.setOmissionFreq(projectId, packId, scenarioId, detailId, freq)
            // Update local scenarioDetails
            const sd = (pack.scenarioDetails || []).find(sd => sd.scenario_id === scenarioId && sd.detail_id === detailId)
            if (sd) sd.omission_freq = freq
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onSetIrrelevantDetails: async (ids) => {
          saving = true; m.redraw()
          try {
            await api.setIrrelevantDetails(projectId, packId, scenarioId, ids)
            // Re-fetch to get inclusion_freq values
            irrelevantDetails = await api.getIrrelevantDetails(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onSetInclusionFreq: async (detailId, freq) => {
          saving = true; m.redraw()
          try {
            await api.setIrrelevantDetailFreq(projectId, packId, scenarioId, detailId, freq)
            const entry = irrelevantDetails.find(d => d.detail_id === detailId)
            if (entry) entry.inclusion_freq = freq
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onCreateIrrelevantDetail: async (data) => {
          saving = true; m.redraw()
          try {
            const detail = await api.createDetail(projectId, packId, data)
            pack.details.push(detail)
            // Add to irrelevant set
            const existingIds = irrelevantDetails.map(d => d.detail_id)
            await api.setIrrelevantDetails(projectId, packId, scenarioId, [...existingIds, detail.id])
            irrelevantDetails = await api.getIrrelevantDetails(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onUpdateIrrelevantDetail: async (detailId, data) => {
          saving = true; m.redraw()
          try {
            const updated = await api.updateDetail(projectId, packId, detailId, data)
            const idx = pack.details.findIndex(d => d.id === detailId)
            if (idx >= 0) pack.details[idx] = updated
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onCreateSnippet: async (data) => {
          saving = true; m.redraw()
          try {
            await api.createNoiseSnippet(projectId, packId, scenarioId, data)
            noiseSnippets = await api.getNoiseSnippets(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onUpdateSnippet: async (snippetId, data) => {
          saving = true; m.redraw()
          try {
            await api.updateNoiseSnippet(projectId, packId, snippetId, data)
            noiseSnippets = await api.getNoiseSnippets(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onDeleteSnippet: async (snippetId) => {
          saving = true; m.redraw()
          try {
            await api.deleteNoiseSnippet(projectId, packId, snippetId)
            noiseSnippets = await api.getNoiseSnippets(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        saving,
      }),
      m('div', { class: formActions }, [
        m('button', {
          class: btnPrimary,
          onclick: () => { setStep(4) },
        }, 'Continue to Style'),
      ]),
    ])
  }

  // --- Step 5: Style ---
  function renderStep5() {
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 5: Style'),
      m('p', { class: stepDescription }, [
        'Style presets control the form and tone of generated scenarios. ',
        m(m.route.Link, { href: `/projects/${projectId}/styles` }, 'Manage Style Presets'),
      ]),
      renderScenarioContext(),
      m(StyleEditor, {
        stylePresets,
        readOnly: true,
      }),
      m('div', { class: formActions }, [
        m('button', {
          class: btnPrimary,
          onclick: () => { setStep(5) },
        }, 'Continue to Generate'),
      ]),
    ])
  }

  // --- Step 6: Generate ---
  function renderStep6() {
    const relevantDetails = (pack.details || []).filter(d => relevantDetailIds.has(d.id))
    return m('div', { class: stepContainer }, [
      m('h2', { class: stepTitle }, 'Step 6: Generate'),
      m('p', { class: stepDescription }, 'Write scenarios yourself or have an LLM generate them.'),
      renderScenarioContext(),
      m('div', { style: `margin-bottom: ${space.xl};` },
        m('button', {
          class: btnPrimary,
          onclick: () => m.route.set(`/projects/${projectId}/scenarios/${packId}/${scenarioId}/write`),
        }, 'Write scenarios'),
      ),
      m(GenerationPanel, {
        generations,
        generationConfig,
        details: relevantDetails,
        criteria,
        stylePresets,
        projectId,
        packId,
        scenarioId,
        onGenerate: async (data) => {
          generating = true
          generationProgress = { completed: 0, total: data.count }
          m.redraw()
          try {
            await api.generate(projectId, packId, scenarioId, data)
            // Final refresh to ensure consistency
            generations = await api.getGenerations(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          generating = false
          generationProgress = null
          m.redraw()
        },
        onDeleteGeneration: async (id) => {
          saving = true; m.redraw()
          try {
            await api.deleteGeneration(projectId, packId, id)
            generations = await api.getGenerations(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          saving = false; m.redraw()
        },
        onGenerateMore: async (id, data) => {
          generating = true
          generationProgress = { completed: 0, total: data.count }
          m.redraw()
          try {
            await api.generateMore(projectId, packId, id, data)
            generations = await api.getGenerations(projectId, packId, scenarioId)
          } catch (e) { error = e.message }
          generating = false
          generationProgress = null
          m.redraw()
        },
        onExport: async (data) => {
          try {
            const result = await api.exportGenerations(projectId, packId, scenarioId, data)
            const isCSV = result.format === 'csv'
            const content = isCSV ? result.data : JSON.stringify(result.data, null, 2)
            const blob = new Blob([content], { type: isCSV ? 'text/csv' : 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `generations.${isCSV ? 'csv' : 'json'}`
            a.click()
            URL.revokeObjectURL(url)
          } catch (e) { error = e.message }
          m.redraw()
        },
        saving,
        generating,
        generationProgress,
      }),
    ])
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      packId = vnode.attrs.packId
      scenarioId = vnode.attrs.scenarioId
      load()
      realtime.connect(projectId)
      unsubProgress = realtime.on('generation_progress', (data) => {
        if (data.scenarioId !== scenarioId) return
        generationProgress = { completed: data.completed, total: data.total }
        // Live-append the new generation if not already present
        if (data.generation && !generations.find(g => g.id === data.generation.id)) {
          generations = [data.generation, ...generations]
        }
        m.redraw()
      })
    },
    onremove() {
      if (unsubProgress) unsubProgress()
    },
    onupdate(vnode) {
      // Handle same-route navigation (different scenario)
      if (vnode.attrs.scenarioId !== scenarioId) {
        scenarioId = vnode.attrs.scenarioId
        load()
      }
    },
    view() {
      if (loading) return m('div', { class: emptyState }, 'Loading...')
      if (!scenario) return m('div', { class: emptyState }, error || 'Scenario not found')

      const packLabel = pack ? pack.label : ''

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, 'Project'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}/scenarios` }, 'Scenarios'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}/scenarios/${packId}` }, packLabel),
          ' / Scenario',
        ]),

        error ? m('div', {
          style: `color: ${colors.critical}; margin-bottom: ${space.md};`,
        }, error) : null,

        m(WizardStepper, {
          steps: getSteps(),
          currentStep: getCurrentStep(),
          onStep: setStep,
        }),

        renderStep(),
      ])
    },
  }
}
